import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { trackAuditCompleted } from "../lib/analytics";
import { usePageSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Calendar } from "../components/ui/calendar";
import { Loader2, ArrowRight, Calendar as CalendarIcon, Check, Clock, TrendingUp, AlertTriangle, Target, BarChart3, Shield, Zap, Phone } from "lucide-react";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

const LABEL_MAP = {
  sales_process: "Sales Process", marketing_process: "Marketing", customer_experience: "Customer Experience",
  operations_admin: "Operations", team_structure: "Team", software_stack: "Tech Stack",
  automation_usage: "Automation", growth_constraints: "Growth", pain_points: "Pain Points",
  ai_readiness: "AI Readiness", revenue_model: "Revenue Model", company_overview: "Company",
};
const LEGACY_KEYS = new Set(["acquire", "convert", "operate", "expand", "defend", "readiness"]);

export default function ScorePreview() {
  const { auditId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageSEO({ title: "Your Blueprint Results", noIndex: true });
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  // Booking state
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingForm, setBookingForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/audit/${auditId}`);
        setAudit(data);
        trackAuditCompleted(auditId, data.scores?.overall);
        if (user) {
          setBookingForm(f => ({ ...f, name: user.name || f.name, email: user.email || f.email }));
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [auditId, user]);

  useEffect(() => {
    if (!selectedDate) return;
    const loadSlots = async () => {
      setSlotsLoading(true);
      try {
        const dateStr = selectedDate.toISOString().split("T")[0];
        const { data } = await api.get(`/booking/available-slots?date=${dateStr}`);
        setAvailableSlots(data.slots || []);
      } catch (e) { console.error(e); }
      finally { setSlotsLoading(false); }
    };
    loadSlots();
  }, [selectedDate]);

  const handleBook = async () => {
    if (!selectedDate || !selectedSlot || !bookingForm.name || !bookingForm.email) return;
    setBooking(true);
    try {
      await api.post("/booking/public", {
        audit_id: auditId,
        date: selectedDate.toISOString().split("T")[0],
        time_slot: selectedSlot,
        name: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone,
        notes: bookingForm.notes,
      });
      setBooked(true);
    } catch (e) { console.error(e); }
    finally { setBooking(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0033CC]" /></div>;

  const scores = audit?.scores || {};
  const sections = scores.sections || {};
  const company = audit?.company_info || {};
  const leakAnalysis = scores.revenue_leak_analysis || {};
  const tierColors = { Reactive: "#EF4444", Emerging: "#F59E0B", Operationalizing: "#818CF8", Systemized: "#10B981", "AI-Native": "#0033CC" };

  // V2 sections only
  const v2Sections = Object.entries(sections).filter(([k]) => !LEGACY_KEYS.has(k)).sort((a, b) => (a[1].percentage || 0) - (b[1].percentage || 0));
  const weakest = v2Sections[0];
  const strongest = v2Sections[v2Sections.length - 1];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="score-preview-page">
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50 px-6 md:px-12">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-16">
          <Link to="/"><img src="/stansbury-logo.png" alt="Stansbury AI" className="h-10 sm:h-12" /></Link>
          {user && <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-sm text-slate-500 font-['Outfit']" data-testid="preview-dashboard-btn">Dashboard</Button>}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16">
        {/* Hero Score */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-[#0033CC] mb-4">Discovery Complete</p>
          <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-medium text-[#0F172A] tracking-tight mb-2">
            {company.company_name ? `${company.company_name}'s` : "Your"} AI Readiness Score
          </h1>
          <div className="inline-flex items-center justify-center w-44 h-44 border-2 border-[#0033CC]/20 bg-white my-8">
            <div className="text-center">
              <div className="text-7xl font-['Outfit'] font-light text-[#0033CC]" data-testid="overall-score">{scores.overall || 0}</div>
              <div className="text-sm text-slate-400 font-['IBM_Plex_Sans']">/100</div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-3 h-3" style={{ backgroundColor: tierColors[scores.tier] || "#818CF8" }} />
            <span className="text-xl font-['Outfit'] font-medium text-[#0F172A]" data-testid="score-tier">{scores.tier}</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Results */}
          <div className="lg:col-span-2 space-y-6">
            {/* Key Metrics */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-3 gap-4" data-testid="key-metrics">
              <div className="bg-white border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-500" /><p className="text-[10px] font-['JetBrains_Mono'] uppercase text-red-500">Revenue Leak</p></div>
                <p className="text-lg font-['Outfit'] font-medium text-red-600">{scores.revenue_leak_severity || "Medium"}</p>
                {leakAnalysis.estimated_annual_leakage && <p className="text-xs text-slate-400 font-['JetBrains_Mono'] mt-1">{leakAnalysis.estimated_annual_leakage}</p>}
              </div>
              <div className="bg-white border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-amber-500" /><p className="text-[10px] font-['JetBrains_Mono'] uppercase text-amber-600">Biggest Gap</p></div>
                <p className="text-lg font-['Outfit'] font-medium text-[#0F172A] capitalize">{LABEL_MAP[weakest?.[0]] || weakest?.[0]?.replace(/_/g, " ") || "—"}</p>
                <p className="text-xs text-slate-400 font-['JetBrains_Mono'] mt-1">{weakest?.[1]?.percentage || 0}%</p>
              </div>
              <div className="bg-white border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-emerald-500" /><p className="text-[10px] font-['JetBrains_Mono'] uppercase text-emerald-600">Strongest</p></div>
                <p className="text-lg font-['Outfit'] font-medium text-[#0F172A] capitalize">{LABEL_MAP[strongest?.[0]] || strongest?.[0]?.replace(/_/g, " ") || "—"}</p>
                <p className="text-xs text-slate-400 font-['JetBrains_Mono'] mt-1">{strongest?.[1]?.percentage || 0}%</p>
              </div>
            </motion.div>

            {/* Discovery Scores */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white border border-slate-200 p-6" data-testid="discovery-scores">
              <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-5">Discovery Scores Across 12 Dimensions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5">
                {v2Sections.map(([k, v]) => {
                  const pct = v.percentage || 0;
                  const color = pct >= 70 ? "#059669" : pct >= 40 ? "#F59E0B" : "#DC2626";
                  return (
                    <div key={k} className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 font-['IBM_Plex_Sans'] w-32 truncate">{LABEL_MAP[k] || k.replace(/_/g, " ")}</span>
                      <div className="flex-1 h-2 bg-slate-100"><div className="h-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} /></div>
                      <span className="text-xs font-['JetBrains_Mono'] w-10 text-right" style={{ color }}>{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Revenue Leak Preview */}
            {leakAnalysis.estimated_annual_leakage && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-[#0F172A] p-6" data-testid="revenue-leak-preview">
                <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-red-400 mb-3">Revenue Leak Estimate</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-2xl font-['Outfit'] font-light text-red-400">{leakAnalysis.estimated_annual_leakage}</p>
                    <p className="text-xs text-slate-500 mt-1">Estimated annual leakage</p>
                  </div>
                  <div>
                    <p className="text-2xl font-['Outfit'] font-light text-white">{leakAnalysis.estimated_weekly_hours_wasted || 0}hrs</p>
                    <p className="text-xs text-slate-500 mt-1">Wasted per week</p>
                  </div>
                  <div>
                    <p className="text-2xl font-['Outfit'] font-light text-amber-400">{leakAnalysis.opportunity_cost || "—"}</p>
                    <p className="text-xs text-slate-500 mt-1">Monthly opportunity cost</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* What's In Your Blueprint */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white border border-slate-200 p-6" data-testid="blueprint-contents">
              <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Your Transformation Blueprint Includes</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: AlertTriangle, label: "Revenue Leak Analysis", desc: "Dollar-quantified breakdown by department" },
                  { icon: Target, label: "AI Opportunity Matrix", desc: "Prioritized initiatives ranked by ROI" },
                  { icon: BarChart3, label: "Department Transformation", desc: "Current vs future state per department" },
                  { icon: TrendingUp, label: "ROI Projections", desc: "Payback period and 3-year returns" },
                  { icon: Shield, label: "Implementation Roadmap", desc: "3-phase execution with dependencies" },
                  { icon: Zap, label: "AI Architecture", desc: "System diagrams and agent specifications" },
                ].map(item => (
                  <div key={item.label} className="flex items-start gap-3 p-3 bg-slate-50">
                    <item.icon className="w-4 h-4 text-[#0033CC] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                      <p className="text-xs font-['Outfit'] font-medium text-[#0F172A]">{item.label}</p>
                      <p className="text-[10px] text-slate-400 font-['IBM_Plex_Sans']">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right: Booking */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-1">
            <div className="bg-white border-2 border-[#0033CC] sticky top-24" data-testid="booking-section">
              {!booked ? (
                <div>
                  <div className="bg-[#0033CC] p-5 text-center">
                    <Phone className="w-5 h-5 text-white mx-auto mb-2" />
                    <h3 className="text-lg font-['Outfit'] font-medium text-white mb-1">Book Your Strategy Call</h3>
                    <p className="text-xs text-white/70 font-['IBM_Plex_Sans']">Walk through your results with a senior advisor</p>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <label className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 mb-1 block">Your Name</label>
                      <Input value={bookingForm.name} onChange={e => setBookingForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" className="rounded-none border-slate-300 h-9 text-sm" data-testid="booking-name" />
                    </div>
                    <div>
                      <label className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 mb-1 block">Email</label>
                      <Input value={bookingForm.email} onChange={e => setBookingForm(f => ({ ...f, email: e.target.value }))} type="email" placeholder="you@company.com" className="rounded-none border-slate-300 h-9 text-sm" data-testid="booking-email" />
                    </div>
                    <div>
                      <label className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 mb-1 block">Phone (Optional)</label>
                      <Input value={bookingForm.phone} onChange={e => setBookingForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 (555) 000-0000" className="rounded-none border-slate-300 h-9 text-sm" data-testid="booking-phone" />
                    </div>
                    <div>
                      <label className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 mb-2 block">Select a Date</label>
                      <div className="flex justify-center">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                          className="rounded-none border border-slate-200"
                        />
                      </div>
                    </div>
                    {selectedDate && (
                      <div>
                        <label className="text-[10px] font-['JetBrains_Mono'] uppercase text-slate-400 mb-2 block">
                          Available Times — {selectedDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                        </label>
                        {slotsLoading ? (
                          <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-[#0033CC]" /></div>
                        ) : availableSlots.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-3">No available slots. Try another date.</p>
                        ) : (
                          <div className="grid grid-cols-3 gap-1.5">
                            {availableSlots.map(slot => (
                              <button key={slot} onClick={() => setSelectedSlot(slot)} className={`text-xs py-2 border transition-colors font-['JetBrains_Mono'] ${selectedSlot === slot ? "bg-[#0033CC] text-white border-[#0033CC]" : "border-slate-200 text-slate-600 hover:border-[#0033CC]"}`} data-testid={`slot-${slot}`}>
                                {slot}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <Button onClick={handleBook} disabled={booking || !selectedDate || !selectedSlot || !bookingForm.name || !bookingForm.email} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-11 font-['Outfit'] font-medium text-sm" data-testid="book-call-btn">
                      {booking ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Booking...</> : <>Book Strategy Call <ArrowRight className="w-4 h-4 ml-2" /></>}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center" data-testid="booking-confirmed">
                  <div className="w-12 h-12 bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-2">Strategy Call Booked</h3>
                  <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-1">
                    {selectedDate?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at {selectedSlot} EST
                  </p>
                  <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-6">
                    We'll walk through your blueprint findings together and design your implementation plan.
                  </p>
                  <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">A confirmation has been sent to {bookingForm.email}</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
